import { FormResponse } from "@archetypes/responses";
import {
  getDataModelPropertyValue,
  isDataModelOrRepeaterValue,
  removeHTMLTagsAndCharacterEntities,
} from "./generic.util";
import { FIELD_ID_SEPERATOR, replaceMentionTagsWithTokenString } from "./formula.util";
import { DataModelValue, ElementType, FormValue, PRECISION } from "@archetypes/form";
import { mathlib } from "@constants/math";

type FormulaValue = number | string | Date | number[] | Date[] | null;

export const isValidMathExpression = (
  expression: string,
  questionsRecord: { questionId: string; questionType: ElementType }[],
) => {
  const tokenString = "question_";
  const scope: Record<string, FormulaValue> = {};
  const cleanExpression = removeHTMLTagsAndCharacterEntities(
    replaceMentionTagsWithTokenString(expression, tokenString),
  );
  questionsRecord.forEach(({ questionId, questionType }) => {
    const isNestedField = questionId.includes(FIELD_ID_SEPERATOR);
    if (questionType === ElementType.DatePicker) {
      scope[tokenString + questionId] = isNestedField ? [new Date()] : new Date();
    } else {
      scope[tokenString + questionId] = isNestedField ? [0] : 0;
    }
  });
  try {
    mathlib.evaluate(cleanExpression, scope);
    return true;
  } catch (err) {
    return false;
  }
};

export const evaluateMathExpression = ({
  expression,
  precision,
  formValues,
  questionsRecord,
}: {
  expression: string;
  precision: PRECISION;
  formValues: FormResponse;
  questionsRecord: { questionId: string; questionType: ElementType }[];
}) => {
  const tokenString = "question_";
  const cleanExpression = removeHTMLTagsAndCharacterEntities(
    replaceMentionTagsWithTokenString(expression, tokenString),
  );
  const scope: Record<string, FormValue> = {};
  questionsRecord.forEach(({ questionId }) => {
    const fieldId = questionId.replaceAll(FIELD_ID_SEPERATOR, "."); // mathlib does not support dot notation in variable names, so we replace it with an underscore
    const questionKey = fieldId.includes(".") ? fieldId.split(".")[0] : fieldId;
    const isDataModelOrRepeaterField =
      fieldId.includes(".") && isDataModelOrRepeaterValue(formValues?.[questionKey]);
    const value = isDataModelOrRepeaterField
      ? getDataModelPropertyValue(
          formValues?.[questionKey] as DataModelValue[],
          fieldId.slice(fieldId.indexOf(".") + 1),
        )
      : formValues[fieldId];
    scope[tokenString + questionId] = value as FormValue;
  });
  try {
    const result = mathlib.evaluate(cleanExpression, scope);

    if (typeof result === "string") {
      return result;
    } else if (typeof result === "number") {
      return Number(result.toFixed(precision));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Error evaluating expression: ${cleanExpression}`, err);
    return null;
  }
};
